import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { messagesApi } from '../services/messagesApi';
import type { CursorPaginatedResponse, Message, SendMessagePayload } from '../services/messagesApi';
import { quickReplyApi } from '../../templates/services/quickReplyApi';
import type { QuickReplyTemplate } from '../../templates/services/quickReplyApi';
import { useTenant } from '../../../providers/TenantProvider';

export const pendingOutboundContacts = new Set<number>();

// ---------------------------------------------------------------------------
// External optimistic message store — survives React Query refetches
// ---------------------------------------------------------------------------
const optimisticStore = new Map<string, Message[]>();
const storeListeners = new Set<() => void>();
const EMPTY_MSGS: Message[] = [];

function sKey(tenantId: number, contactId: number) {
  return `${tenantId}:${contactId}`;
}

function notifyStore() {
  storeListeners.forEach((fn) => fn());
}

function storeSubscribe(cb: () => void) {
  storeListeners.add(cb);
  return () => { storeListeners.delete(cb); };
}

function addToStore(key: string, msg: Message) {
  optimisticStore.set(key, [...(optimisticStore.get(key) ?? []), msg]);
  notifyStore();
}

function replaceInStore(key: string, oldId: number, real: Message) {
  const cur = optimisticStore.get(key);
  if (!cur) return;
  optimisticStore.set(key, cur.map((m) => (m.id === oldId ? real : m)));
  notifyStore();
}

function patchInStore(key: string, msgId: number, patch: Partial<Message>) {
  const cur = optimisticStore.get(key);
  if (!cur) return;
  optimisticStore.set(key, cur.map((m) => (m.id === msgId ? { ...m, ...patch } : m)));
  notifyStore();
}

/**
 * Wipe all module-level optimistic state. Called on tenant switch / logout so a
 * hung 'sending' message or a `pendingOutboundContacts` entry from a previous
 * tenant can't linger (which would also permanently suppress the Pusher
 * `message.new` echo for that contact — see usePusherEvents). [FE-REL-04/FE-MNT-04]
 */
export function resetOptimisticState() {
  optimisticStore.clear();
  pendingOutboundContacts.clear();
  notifyStore();
}

export function resolveOptimisticByIds(tenantId: number, contactId: number, serverIds: Set<number>) {
  const key = sKey(tenantId, contactId);
  const cur = optimisticStore.get(key);
  if (!cur) return;
  const next = cur.filter((m) => !serverIds.has(m.id));
  if (next.length === cur.length) return;
  if (next.length === 0) optimisticStore.delete(key);
  else optimisticStore.set(key, next);
  notifyStore();
}

export function useOptimisticMessages(tenantId: number | null, contactId: number | null): Message[] {
  const key = tenantId && contactId ? sKey(tenantId, contactId) : '';
  return useSyncExternalStore(
    storeSubscribe,
    () => (key ? optimisticStore.get(key) ?? EMPTY_MSGS : EMPTY_MSGS),
  );
}

// ---------------------------------------------------------------------------
// Query hook
// ---------------------------------------------------------------------------
export type MessagesPage = CursorPaginatedResponse<Message>;

/**
 * Cursor pagination for chat messages. Implemented manually (not via
 * useInfiniteQuery) because @tanstack/react-query 5.94.4's InfiniteQueryObserver
 * declares class-field shadows that break the prototype chain under modern
 * class-field semantics.
 *
 * Server returns pages newest-first with the cursor walking backwards in time.
 * The flattened list is oldest → newest so the chat scrolls naturally.
 */
export function useMessages(contactId: number | null) {
  const { currentTenantId } = useTenant();
  const queryClient = useQueryClient();

  // Pages held newest-first (page[0] is the freshest batch). Cursor points to
  // the NEXT older page to load.
  const [pages, setPages] = useState<MessagesPage[]>([]);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const loadedContactRef = useRef<number | null>(null);

  // Fetch the first (newest) page whenever the tenant or contact changes.
  const firstPageQuery = useQuery({
    queryKey: ['messages-infinite', currentTenantId, contactId],
    queryFn: async () => (await messagesApi.list(contactId!)).data,
    enabled: !!currentTenantId && !!contactId,
  });

  useEffect(() => {
    if (firstPageQuery.data && loadedContactRef.current !== contactId) {
      setPages([firstPageQuery.data]);
      loadedContactRef.current = contactId ?? null;
    } else if (firstPageQuery.data && pages[0] !== firstPageQuery.data) {
      // Server refetch (e.g. after Pusher invalidation) — replace page 0 but
      // keep older pages that the user scrolled into.
      setPages((prev) => (prev.length === 0 ? [firstPageQuery.data!] : [firstPageQuery.data!, ...prev.slice(1)]));
    }
  }, [firstPageQuery.data, contactId]);

  // Reset cached pages immediately when the active contact changes so the
  // stale history from the previous conversation can't briefly render.
  useEffect(() => {
    if (loadedContactRef.current !== contactId) {
      setPages([]);
    }
  }, [contactId]);

  const nextCursor = pages.length > 0 ? pages[pages.length - 1].next_cursor : null;
  const hasNextPage = !!nextCursor;

  const fetchNextPage = useCallback(async () => {
    if (!contactId || !currentTenantId || !nextCursor || isFetchingNextPage) return;
    setIsFetchingNextPage(true);
    try {
      const res = await messagesApi.list(contactId, nextCursor);
      setPages((prev) => [...prev, res.data]);
    } finally {
      setIsFetchingNextPage(false);
    }
  }, [contactId, currentTenantId, nextCursor, isFetchingNextPage]);

  // Allow external code (Pusher handler, send mutation) to invalidate just the
  // newest page without nuking pagination state.
  const invalidateNewest = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['messages-infinite', currentTenantId, contactId] });
  }, [queryClient, currentTenantId, contactId]);

  // Flatten pages oldest → newest. pages[0] is the newest batch.
  const messages = useMemo<Message[]>(() => {
    if (pages.length === 0) return [];
    const out: Message[] = [];
    for (let i = pages.length - 1; i >= 0; i--) {
      const page = pages[i].data;
      for (let j = page.length - 1; j >= 0; j--) out.push(page[j]);
    }
    return out;
  }, [pages]);

  return {
    messages,
    isLoading: firstPageQuery.isLoading && pages.length === 0,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    invalidateNewest,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function makeOptimisticMessage(
  contactId: number,
  tenantId: number,
  overrides: Partial<Message>,
): Message {
  return {
    id: -(Date.now() + Math.random()),
    tenant_id: tenantId,
    contact_id: contactId,
    wa_message_id: null,
    contact_wa_id: null,
    direction: 'outbound',
    status: 'sending',
    type: 'text',
    body: null,
    interactive_payload: null,
    raw_payload: null,
    sent_at: new Date().toISOString(),
    created_at: new Date().toISOString(),
    media_url: null,
    media_mime_type: null,
    media_filename: null,
    media_size: null,
    context_wa_message_id: null,
    is_edited: false,
    edit_history: null,
    is_revoked: false,
    reactions: null,
    ...overrides,
  };
}

// ---------------------------------------------------------------------------
// Send text / interactive / template
// ---------------------------------------------------------------------------
export function useSendMessage(contactId: number | null) {
  const queryClient = useQueryClient();
  const { currentTenantId } = useTenant();
  const queryKey = ['messages-infinite', currentTenantId, contactId];

  return useMutation({
    mutationFn: (data: SendMessagePayload) => messagesApi.send(contactId!, data),
    onMutate: (payload) => {
      if (!contactId || !currentTenantId) return;
      pendingOutboundContacts.add(contactId);
      const key = sKey(currentTenantId, contactId);
      const optimistic = makeOptimisticMessage(contactId, currentTenantId, {
        type: payload.type ?? 'text',
        body: payload.body ?? null,
        context_wa_message_id: payload.context_message_id ?? null,
        interactive_payload: payload.interactive ?? payload.template ?? null,
      });
      addToStore(key, optimistic);
      return { key, optimisticId: optimistic.id };
    },
    onSuccess: (response, _vars, ctx) => {
      if (contactId) pendingOutboundContacts.delete(contactId);
      if (ctx) replaceInStore(ctx.key, ctx.optimisticId, response.data);
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
    onError: (_err, _vars, ctx) => {
      if (contactId) pendingOutboundContacts.delete(contactId);
      if (ctx) patchInStore(ctx.key, ctx.optimisticId, { status: 'failed' });
    },
  });
}

// ---------------------------------------------------------------------------
// Send media / file / voice
// ---------------------------------------------------------------------------
export interface SendMediaArgs {
  file: File;
  type: 'image' | 'video' | 'audio' | 'document';
  caption?: string;
  context_message_id?: string;
}

let _mediaOptMeta: { key: string; id: number } | null = null;

export function useSendMedia(contactId: number | null) {
  const queryClient = useQueryClient();
  const { currentTenantId } = useTenant();
  const queryKey = ['messages-infinite', currentTenantId, contactId];

  return useMutation({
    mutationFn: (args: SendMediaArgs) => {
      const meta = _mediaOptMeta;
      const formData = new FormData();
      formData.append('file', args.file);
      formData.append('type', args.type);
      if (args.caption) formData.append('caption', args.caption);
      if (args.context_message_id)
        formData.append('context_message_id', args.context_message_id);

      return messagesApi.sendMedia(contactId!, formData, (pct) => {
        if (meta) patchInStore(meta.key, meta.id, { _uploadProgress: pct });
      });
    },
    onMutate: (args) => {
      if (!contactId || !currentTenantId) return;
      pendingOutboundContacts.add(contactId);
      const key = sKey(currentTenantId, contactId);

      const localUrl = URL.createObjectURL(args.file);
      const optimistic = makeOptimisticMessage(contactId, currentTenantId, {
        type: args.type,
        body: args.caption ?? null,
        media_url: 'pending',
        media_mime_type: args.file.type,
        media_filename: args.file.name,
        media_size: args.file.size,
        context_wa_message_id: args.context_message_id ?? null,
        _localMediaUrl: localUrl,
        _uploadProgress: 0,
      });

      _mediaOptMeta = { key, id: optimistic.id };
      addToStore(key, optimistic);
      return { key, optimisticId: optimistic.id, localUrl };
    },
    onSuccess: (response, _vars, ctx) => {
      if (contactId) pendingOutboundContacts.delete(contactId);
      if (ctx?.localUrl) URL.revokeObjectURL(ctx.localUrl);
      if (ctx) replaceInStore(ctx.key, ctx.optimisticId, response.data);
      _mediaOptMeta = null;
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
    onError: (_err, _vars, ctx) => {
      if (contactId) pendingOutboundContacts.delete(contactId);
      if (ctx) patchInStore(ctx.key, ctx.optimisticId, { status: 'failed' });
      _mediaOptMeta = null;
    },
  });
}

// ---------------------------------------------------------------------------
// Send reaction
// ---------------------------------------------------------------------------
export function useSendReaction(contactId: number | null) {
  const queryClient = useQueryClient();
  const { currentTenantId } = useTenant();

  return useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: number; emoji: string }) =>
      messagesApi.sendReaction(contactId!, messageId, emoji),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['messages-infinite', currentTenantId, contactId],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Template queries
// ---------------------------------------------------------------------------
export function useTemplates() {
  const { currentTenantId } = useTenant();

  return useQuery({
    queryKey: ['templates', currentTenantId],
    queryFn: () => messagesApi.templates(),
    select: (res) => res.data.data,
    enabled: !!currentTenantId,
    staleTime: 60_000,
  });
}

export function useQuickReplyTemplates() {
  const { currentTenantId } = useTenant();

  return useQuery({
    queryKey: ['quick-reply-templates', currentTenantId],
    queryFn: () => quickReplyApi.list(),
    select: (res: { data: { data: QuickReplyTemplate[] } }) => res.data.data.filter((t) => !t.is_auto_reply),
    enabled: !!currentTenantId,
    staleTime: 60_000,
  });
}
